import React, { ChangeEvent, useState } from 'react';

import Alert from '@vromo/ui/components/Alert';
import Input from '@vromo/ui/components/Input';
import Modal from '@vromo/ui/components/Modal';
import Title from '@vromo/ui/components/Title';

import { useTranslation } from 'react-i18next';
import { deserialize, serialize, update } from 'serializr';

import { action } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';

import { defaultRule, EntityList, entityListSchema, Policy, PolicyRule, Site } from '@vromo/models';

import Button from '@vromo/ui/components/Button';
import Checkbox from '@vromo/ui/components/Checkbox';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { ModalActions, ModalClose, ModalContent, ModalHeader } from '@vromo/ui/components/Modal';

import RuleEditor from './RuleEditor';

import { useStore } from '@vromo/mobx-react';
import { PolicyCapacity } from '@vromo/models';
import Flex from '@vromo/ui/components/Flex';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import Select from '@vromo/ui/components/Select2';
import { capitalize, sortBy } from 'lodash';
import { IViewModel } from 'mobx-utils';
import { RootStore } from '../stores/RootStore';
import CapacityEditor from './CapacityEditor';
import Rules from './components/Rules';
import { ActionButton, ClearError, RuleControl, RuleTab } from './components/styled';
import { qaLabelAutodispatcher } from '../../../../common-qa-labels/autodispatcher';

interface PolicyFormProps {
    deletePolicy: (id: number) => void;
    loading?: boolean;
    onCancel: () => void;
    policy: Policy & IViewModel<Policy>;
    policyInUse: (id: number) => Promise<EntityList<Policy>>;
    savePolicy: (p: Policy) => void;
    teamOptions: Array<{ label: string | null; value: number | boolean | null }>;
}

type EditedRule = {
    index: number;
    isNew: boolean;
    rule: PolicyRule;
};

const PolicyForm = observer<PolicyFormProps>(function PolicyForm({
    policy,
    policyInUse,
    savePolicy,
    deletePolicy,
    onCancel,
    teamOptions,
    loading
}) {
    const { siteStore, authStore: { user } } = useStore<RootStore>('rootStore');

    const [currentRule, setCurrentRule] = useState<EditedRule | null>(null);
    const [showAlert, setShowAlert] = useState(false);
    const [checkingPolicyUse, setCheckingPolicyUse] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicatePolicyName, setDuplicatePolicyName] = useState('');
    const [selectedDuplicateTeam, setSelectedDuplicateTeam] = useState<number | undefined | null>();
    const { t } = useTranslation();

    const ruleList = useLocalObservable(() =>
        !policy.rules
            ? new EntityList(PolicyRule)
            : (deserialize(
                  entityListSchema(PolicyRule),
                  policy.rules
              ) as unknown as EntityList<PolicyRule>)
    );
    const [capacity, setCapacity] = useState(policy.capacity);

    const [unableToDelete, setUnableToDelete] = useState(true);
    const [sitesUsingPolicy, setSitesUsingPolicy] = useState([] as Site[]);

    const [showCapacityEditor, setShowCapacityEditor] = useState(false);

    const saveCapacity = (newCapacity: PolicyCapacity) => {
        setCapacity(newCapacity);
        setShowCapacityEditor(false);
    };

    const saveRule = action((rule: PolicyRule) => {
        if (currentRule!.isNew) {
            ruleList.items.push(rule);
        } else {
            update(ruleList.items[currentRule!.index], rule);
        }
        setCurrentRule(null);
    });

    const editRule = (rule: PolicyRule, index: number, isNew: boolean) =>
        setCurrentRule({ rule: rule, index: index, isNew: isNew });

    const deleteRule = action((rule: PolicyRule) => ruleList.items.remove(rule));

    const isValidPolicy = (policy: Policy) => policy?.name?.trim().length;

    const savePolicyForm = action((policy: Policy & IViewModel<Policy>) => {
        if (isValidPolicy(policy)) {
            policy.rules = deserialize(entityListSchema(PolicyRule), ruleList);
            policy.capacity = capacity;

            policy.submit();
            savePolicy(policy.model);
        } else {
            setShowAlert(true);
        }
    });

    const handleModalClose = () => {
        policy.reset();
        onCancel();
    };

    const handlePolicyNameChange = action(
        (e: ChangeEvent<HTMLInputElement>) => (policy.name = e.target.value)
    );

    const handleAddNewRule = () => {
        editRule(deserialize(PolicyRule, defaultRule()), ruleList.items.length, true);
    };

    const handleClickCapacityLimitToggle = () => setShowCapacityEditor((v) => !v);

    const openDeleteModal = async () => {
        setCheckingPolicyUse(true);
        setShowDeleteModal(true);

        async function checkPolicy() {
            const serializedPolicy = await policyInUse(policy.id!);
            const fullPolicy = deserialize(Policy, serializedPolicy);
            setUnableToDelete(fullPolicy.isInUse);
            setSitesUsingPolicy((fullPolicy.sites && (fullPolicy.sites.items as Site[])) || []);
        }

        await checkPolicy();
        setCheckingPolicyUse(false);
    };

    const closeDeleteModal = () => setShowDeleteModal(false);

    const handleDuplicateModalToggle = () => setShowDuplicateModal((v) => !v);

    const handleDeleteClick = () => {
        deletePolicy(policy.id!);
        onCancel();
    };

    const handleSaveClick = () => savePolicyForm(policy);

    function copyPolicy(name: string, team?: number) {
        if (isValidPolicy(policy)) {
            const copiedPolicy = deserialize(Policy, serialize(policy.model)) as unknown as Policy;
            copiedPolicy.id = null;
            copiedPolicy.rules = deserialize(
                entityListSchema(PolicyRule),
                ruleList
            ) as unknown as EntityList<PolicyRule>;

            copiedPolicy.name = name;
            copiedPolicy.capacity = capacity;

            if (team) {
                copiedPolicy.rules.forEach((rule) => {
                    rule.team = team;
                });
            }

            savePolicy(copiedPolicy);
        } else {
            setShowAlert(true);
        }
    }

    const togglePolicyMode = action(
        () => (policy.mode = policy.mode === 'underway' ? 'assign' : 'underway')
    );

    const toggleSingleOverflowDispatch = action(
        () => (policy.singleOverflowDispatch = !policy?.singleOverflowDispatch)
    );

    if (showCapacityEditor) {
        return (
            <div>
                <CapacityEditor
                    addCapacity={saveCapacity}
                    cancel={handleClickCapacityLimitToggle}
                    capacity={capacity}
                    teamOptions={teamOptions}
                />
            </div>
        );
    }

    if (currentRule === null) {
        return (
            <div>
                <ModalClose onClose={handleModalClose} />
                <ModalHeader>
                    <Title data-testid={qaLabelAutodispatcher.autoDispatcherModal.title}>
                        {capitalize(policy.id ? t('common.edit') : t('common.new'))}{' '}
                        {t('policyForm.autoDispatch')}
                    </Title>
                </ModalHeader>
                <ModalContent>
                    {showAlert && (
                        <Alert danger>
                            <Flex horizontal="between" vertical="middle">
                                {t('policyForm.errorMessage')}
                                <ClearError
                                    icon="times"
                                    onClick={() => {
                                        setShowAlert(false);
                                    }}
                                />
                            </Flex>
                        </Alert>
                    )}
                    <div>
                        {policy.id && (
                            <>
                                <label data-testid={qaLabelAutodispatcher.autoDispatcherModal.idLabel}>ID</label>
                                <div style={{ marginBottom: 20 }}>{policy.id}</div>
                            </>
                        )}
                        <Input data-testid={qaLabelAutodispatcher.autoDispatcherModal.policyNameInput}
                            label={t('policyForm.policyName')}
                            onChange={handlePolicyNameChange}
                            type="text"
                            value={policy.name || ''}
                        />
                    </div>
                    <div data-testid="checkbox-underway-div" style={{ display: 'flex', marginTop: 20 }}>
                        <Checkbox data-testid={qaLabelAutodispatcher.autoDispatcherModal.underwayCheckbox}
                            checked={policy.mode === 'underway'}
                            label={t('policyForm.underway')}
                            onChange={togglePolicyMode}
                        />
                    </div>
                    {user?.contact?.email?.endsWith('@vromo.io') && (
                        <div
                            data-testid="checkbox-single-overflow-div"
                            style={{ display: 'flex', marginTop: 20 }}>
                            <Checkbox
                                data-testid={
                                    qaLabelAutodispatcher.autoDispatcherModal
                                        .singleOverflowDispatchCheckbox
                                }
                                checked={!!policy.singleOverflowDispatch}
                                label={t('policyForm.singleOverflowDispatch')}
                                onChange={toggleSingleOverflowDispatch}
                            />
                            <div style={{ marginLeft: 6, marginTop: 8, fontSize: '8px' }}>
                                (VROMO internal only)
                            </div>
                        </div>
                    )}
                    <Flex horizontal="between" vertical="middle" style={{ marginTop: 20 }}>
                        <h3>{t('policyForm.rules')}</h3>
                    </Flex>
                    <div style={{ marginBottom: 10, maxHeight: 400, overflow: 'auto' }}>
                        <Rules
                            rules={ruleList}
                            teams={teamOptions}
                            editRule={editRule}
                            deleteRule={deleteRule}
                        />
                    </div>
                    <Flex vertical="middle">
                        <Button data-testid={qaLabelAutodispatcher.autoDispatcherModal.addNewRuleBtn} onClick={handleAddNewRule}>
                            <FontAwesome icon="plus" /> {t('policyForm.add')}
                        </Button>
                    </Flex>
                    <Flex horizontal="between" vertical="middle" style={{ marginTop: 20 }}>
                        <h3>{t('policyForm.capacity')}</h3>
                    </Flex>
                    {capacity ? (
                        <RuleTab>
                            <Flex horizontal="between">
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        flex: 1,
                                        flexWrap: 'wrap',
                                        marginRight: 20
                                    }}>
                                    Maximum of &nbsp;<b>{capacity.maxLoad} jobs</b>&nbsp; per
                                    available driver
                                </div>
                                <RuleControl>
                                    <FontAwesome
                                        icon="edit"
                                        onClick={handleClickCapacityLimitToggle}
                                    />
                                    <FontAwesome
                                        icon="trash-alt"
                                        onClick={() => setCapacity(undefined)}
                                    />
                                </RuleControl>
                            </Flex>
                        </RuleTab>
                    ) : (
                        <Flex vertical="middle">
                            <Button onClick={handleClickCapacityLimitToggle}>
                                <FontAwesome icon="plus" /> {t('policyForm.addLimit')}
                            </Button>
                        </Flex>
                    )}
                </ModalContent>
                <ModalActions>
                    {policy.id ? (
                        <>
                            <ActionButton disabled={loading} destructive onClick={openDeleteModal} data-testid={qaLabelAutodispatcher.autoDispatcherModal.deleteBtn}>
                                {t('common.delete')}
                            </ActionButton>
                            <ActionButton
                                disabled={loading}
                                primary
                                style={{ marginRight: 'auto' }}
                                onClick={handleDuplicateModalToggle}>
                                {t('common.duplicate')}
                            </ActionButton>
                        </>
                    ) : null}

                    <ActionButton data-testid={qaLabelAutodispatcher.autoDispatcherModal.cancelBtn}
                        disabled={loading} 
                        onClick={handleModalClose}>
                        {t('common.cancel')}
                    </ActionButton>
                    <ActionButton data-testid={qaLabelAutodispatcher.autoDispatcherModal.saveBtn}
                        primary
                        onClick={handleSaveClick}
                        disabled={loading}
                        loading={loading}>
                        {t('common.save')}
                    </ActionButton>
                </ModalActions>
                <Modal isOpen={showDeleteModal} onRequestClose={closeDeleteModal}>
                    {unableToDelete ? (
                        <>
                            {!checkingPolicyUse ? (
                                <>
                                    <ModalHeader>
                                        <Title style={{ marginTop: 0 }}>
                                            {t('policyForm.delete.unable.header')}
                                        </Title>
                                    </ModalHeader>
                                    <ModalContent>
                                        <p>{t('policyForm.delete.unable.message')}</p>
                                        {sitesUsingPolicy.map((site: Site) => {
                                            const name = siteStore.withId(site.id!)?.contact?.name;
                                            return (
                                                <li key={site.id}>
                                                    {name
                                                        ? name
                                                        : t('policyForm.delete.unable.site', {
                                                              id: site.id
                                                          })}
                                                </li>
                                            );
                                        })}
                                    </ModalContent>
                                    <ModalActions>
                                        <Button onClick={closeDeleteModal}>{t('common.ok')}</Button>
                                    </ModalActions>
                                </>
                            ) : (
                                <Flex>
                                    {t('policyForm.delete.checkUsage')}
                                    <LoadingIndicator />
                                </Flex>
                            )}
                        </>
                    ) : (
                        <>
                            <ModalHeader>
                                <Title style={{ marginTop: 0 }} data-testid={qaLabelAutodispatcher.autoDispatcherModal.deleteAutodispatcherModal.title}>
                                    {t('policyForm.delete.header')}
                                </Title>
                            </ModalHeader>
                            <ModalContent>
                                <p data-testid={qaLabelAutodispatcher.autoDispatcherModal.deleteAutodispatcherModal.message}>
                                    {t('policyForm.delete.message')}
                                </p>
                            </ModalContent>
                            <ModalActions>
                                <Button onClick={closeDeleteModal}>{t('common.cancel')}</Button>
                                <Button destructive onClick={handleDeleteClick} data-testid={qaLabelAutodispatcher.autoDispatcherModal.deleteAutodispatcherModal.deleteBtn}>
                                    {t('common.delete')}
                                </Button>
                            </ModalActions>
                        </>
                    )}
                </Modal>
                <Modal isOpen={showDuplicateModal} onRequestClose={handleDuplicateModalToggle}>
                    <ModalHeader>
                        <Title style={{ marginTop: 0 }}>{t('policyForm.copy.header')}</Title>
                    </ModalHeader>
                    <ModalContent>
                        <Input
                            label="Name"
                            type="text"
                            value={duplicatePolicyName}
                            onChange={(e) => setDuplicatePolicyName(e.target.value)}
                        />
                        <p>{t('policyForm.copy.message')}</p>
                        <Select
                            value={
                                selectedDuplicateTeam
                                    ? teamOptions.find(
                                          (t: { value: any }) => t.value === selectedDuplicateTeam
                                      )
                                    : null
                            }
                            onChange={({ value }: { value: number }) =>
                                setSelectedDuplicateTeam(value)
                            }
                            options={[
                                { label: '', value: '' },
                                ...sortBy(teamOptions, (t) => t.label)
                            ]}
                        />
                    </ModalContent>
                    <ModalActions>
                        <Button onClick={handleDuplicateModalToggle}>{t('common.cancel')}</Button>
                        <Button
                            disabled={!duplicatePolicyName}
                            primary
                            onClick={() => {
                                if (duplicatePolicyName) {
                                    copyPolicy(duplicatePolicyName, selectedDuplicateTeam!);
                                    onCancel();
                                }
                            }}>
                            {t('common.copy')}
                        </Button>
                    </ModalActions>
                </Modal>
            </div>
        );
    } else {
        return (
            <div>
                <RuleEditor
                    addRule={saveRule}
                    cancel={() => setCurrentRule(null)}
                    defaultRule={defaultRule()}
                    rule={currentRule.rule}
                    teamOptions={teamOptions}
                />
            </div>
        );
    }
});

export default PolicyForm;
